import React, {useMemo, useCallback, useState, useEffect, useRef} from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  Collapse,
  HStack,
  Flex,
  useTheme,
  Center,
  Image,
  Checkbox,
} from '@chakra-ui/react';
import {useInfiniteGlobalSearchWithParamsAndLocation} from '../../../hooks/useFetchGlobalSearch';
import {useInfiniteMeetups} from '../../../hooks/useMeetUps';
import useGetUserLocation from '../../../hooks/useUserLocation';
import MapView from '../../../components/MapView';
import OfferCard from '../../../components/Articles/Offers/OfferCard';
import PlaceCard from '../../../components/Articles/PlaceCard';
import EventCard from '../../../components/Articles/EventCard';
import {SpinnerLoader} from '../../../components/Loader';
import {calculateDistance, getDistanceString} from '../../../utils/distance';
import {Offer, Event, Place} from '../../../types/article';
import MeetUpCard from '../../../components/Articles/MeetUpCard';
import {TextG16} from '../../../components/typography';
import PartnerCard from '../../../components/Articles/PartnerCard';
import {
  getDirectionsLink,
  getDirectionsLinkFromPlace,
  getDirectionsLinkFromEvent,
} from '../../../utils/address';
import {BlueButton, WhiteButton} from '../../../components/button';
import {useNavigate} from 'react-router-dom';

// Helper function to get directions link for any item type
const getDirectionsLinkForItem = (item: NumberedListObject): string | undefined => {
  // If item has direct coordinates, use them
  if (item.latitude && item.longitude) {
    return getDirectionsLink({latitude: item.latitude, longitude: item.longitude});
  }

  // Try type-specific approaches
  if (item.type === 'event' || item.originalType === 'event') {
    return getDirectionsLinkFromEvent(item.data as Event);
  }

  if (item.type === 'place' || item.originalType === 'place' || item.category) {
    return getDirectionsLinkFromPlace(item.data as Place);
  }

  // For offers, try to get location from associated place or event
  if (item.type === 'offer' || item.type === 'offers' || item.type === 'special-offers') {
    const offer = item.data as Offer;
    if (offer.place) {
      return getDirectionsLinkFromPlace(offer.place);
    }
    if (offer.event) {
      return getDirectionsLinkFromEvent(offer.event);
    }
  }

  return undefined;
};

interface CuratedEventContentProps {
  topicId?: string; // Made optional for custom data loading
  config: CuratedEventConfig;
  customDataLoader?: CustomDataLoader; // New: for custom data loading like Farmers Market
  event?: Event; // Optional event data to extract partners from
}

interface CustomDataLoader {
  loadOffers?: (page?: number) => Promise<any[]> | any[];
  loadEvents?: () => Promise<any[]> | any[];
  loadMeetups?: () => Promise<any[]> | any[];
  loadPlaces?: () => Promise<any[]> | any[];
  loadPartners?: () => Promise<any[]> | any[];
}

export enum directionsLinkStyles {
  BUTTON = 'button',
  TEXT = 'text',
}

export enum listItemStyles {
  CARD = 'card',
  TEXT = 'text',
}

interface CuratedEventConfig {
  directionsLinkStyle?: directionsLinkStyles;
  listItemStyle?: listItemStyles;
  title?: string;
  bannerImage?: string;
  bannerBackgroundColor?: string;
  bannerTextColor?: string;
  sections: {
    events: SectionConfig;
    meetups: SectionConfig;
    offers: SectionConfig;
    partners: SectionConfig;
    places: SectionConfig;
    [key: string]: SectionConfig; // Allow custom sections like parking, murals, etc.
  };
  layout: 'list' | 'map' | 'map-and-list'; // Support map above list
  mapConfig?: {
    enabled: boolean;
    title?: string;
    center?: {latitude: number; longitude: number};
    initialZoom?: number;
    height?: string;
    showUserLocation?: boolean;
  };
  dateFilter?: {
    enabled: boolean;
    referenceEvent?: Event;
    mode: 'same-day' | 'date-range' | 'none';
  };
  distanceFilter?: {
    enabled: boolean;
    maxDistance: number; // in miles
    centerLocation: {latitude: number; longitude: number};
  };
  customCategorization?: {
    enabled: boolean;
    categoryFunction?: (item: any) => string;
    placeTypeMapping?: Record<number, string>;
  };
}

interface SectionConfig {
  enabled: boolean;
  title: string;
  icon: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  hoverColor: string;
  circleColor: string;
  circleBackgroundImage?: string; // Optional gradient support for circle backgrounds
  description: string;
  sortBy?: 'distance' | 'date' | 'alphabetical';
  checkboxEnabled?: boolean; // Enable checkboxes for this section
  customHeaderContent?: {
    showInsiderClubPromo?: boolean;
    showPreviewButton?: boolean;
    previewButtonText?: string;
    previewExpanded?: boolean;
    onPreviewToggle?: () => void;
    membershipUrl?: string;
  };
}

interface NumberedListObject {
  id: string | number;
  type: 'offer' | 'place' | 'event' | 'partner' | string; // Allow custom types
  number: number;
  latitude?: number;
  longitude?: number;
  name: string;
  data: any;
  is_meetup?: boolean;
  category?: string; // For custom categorization
  customIcon?: string; // For custom icons like "P" for parking
  originalType?: string; // Preserve original data type for card rendering when using custom categories
}

const CuratedEventContent: React.FC<CuratedEventContentProps> = ({
  topicId,
  config,
  customDataLoader,
  event,
}) => {
  const theme = useTheme();
  const userLocation = useGetUserLocation();
  const navigate = useNavigate();

  // Get directions link style with default
  const getDirectionsLinkStyle = () => config.directionsLinkStyle || directionsLinkStyles.BUTTON;

  // Get list item style with default
  const getListItemStyle = () => config.listItemStyle || listItemStyles.CARD;

  // Section expanded states (collapsed by default)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Checked items state (persisted in localStorage)
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('curatedEventCheckedItems');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Function to toggle section expansion
  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  // Function to toggle item checked state
  const toggleItemChecked = useCallback((itemId: string | number) => {
    setCheckedItems((prev) => {
      const newCheckedItems = {
        ...prev,
        [itemId]: !prev[itemId],
      };
      // Persist to localStorage
      try {
        localStorage.setItem('curatedEventCheckedItems', JSON.stringify(newCheckedItems));
      } catch (error) {
        console.warn('Failed to save checked items to localStorage:', error);
      }
      return newCheckedItems;
    });
  }, []);

  // Stabilize user location data
  const stableUserLocation = useMemo(() => {
    if (!userLocation?.data?.latitude || !userLocation?.data?.longitude) {
      return {latitude: undefined, longitude: undefined};
    }
    return {
      latitude: userLocation.data.latitude,
      longitude: userLocation.data.longitude,
    };
  }, [userLocation?.data?.latitude, userLocation?.data?.longitude]);

  // Memoize sort objects
  const offerSort = useMemo(() => ({by: 'ends_at', direction: 'asc'}), []);
  const placeSort = useMemo(() => ({by: 'default', direction: 'asc'}), []);
  const eventSort = useMemo(() => ({by: 'start_date_time', direction: 'asc'}), []);

  // Helper function to sort items by distance from user location
  const sortByDistanceFromUser = useCallback(
    (items: NumberedListObject[]) => {
      if (!stableUserLocation.latitude || !stableUserLocation.longitude) {
        return items; // Return unsorted if no user location
      }

      return [...items].sort((a, b) => {
        if (!a.latitude || !a.longitude || !b.latitude || !b.longitude) {
          return 0;
        }

        const userLocationObj = {
          latitude: stableUserLocation.latitude!,
          longitude: stableUserLocation.longitude!,
        };

        const distanceA = calculateDistance(userLocationObj, {
          latitude: a.latitude,
          longitude: a.longitude,
        });

        const distanceB = calculateDistance(userLocationObj, {
          latitude: b.latitude,
          longitude: b.longitude,
        });

        return distanceA - distanceB;
      });
    },
    [stableUserLocation.latitude, stableUserLocation.longitude]
  );

  // Search parameters for different content types (disabled when using custom loader)
  const offersSearchParams = useMemo(() => {
    if (customDataLoader || !topicId) {
      return {
        type: 'offer',
        lat: 0,
        long: 0,
        'topics[]': 'disabled',
        term: '',
        orderBy: 'ends_at',
        sort: offerSort,
        disabled: true, // Flag to indicate this search should be disabled
      };
    }
    return {
      type: 'offer',
      lat: stableUserLocation.latitude,
      long: stableUserLocation.longitude,
      'topics[]': topicId,
      term: '',
      orderBy: 'ends_at',
      sort: offerSort,
    };
  }, [
    customDataLoader,
    topicId,
    stableUserLocation.latitude,
    stableUserLocation.longitude,
    offerSort,
  ]);

  const eventsSearchParams = useMemo(() => {
    if (customDataLoader || !topicId) {
      return {
        type: 'event',
        lat: 0,
        long: 0,
        'topics[]': 'disabled',
        term: '',
        orderBy: 'starts_at',
        sort: eventSort,
        disabled: true, // Flag to indicate this search should be disabled
      };
    }
    return {
      type: 'event',
      lat: stableUserLocation.latitude,
      long: stableUserLocation.longitude,
      'topics[]': topicId,
      term: '',
      orderBy: 'starts_at',
      sort: eventSort,
    };
  }, [
    customDataLoader,
    topicId,
    stableUserLocation.latitude,
    stableUserLocation.longitude,
    eventSort,
  ]);

  const placesSearchParams = useMemo(() => {
    if (customDataLoader || !topicId) {
      return {
        type: 'place',
        lat: 0,
        long: 0,
        'topics[]': 'disabled',
        term: '',
        orderBy: 'default',
        sort: placeSort,
        disabled: true, // Flag to indicate this search should be disabled
      };
    }
    return {
      type: 'place',
      lat: stableUserLocation.latitude,
      long: stableUserLocation.longitude,
      'topics[]': topicId,
      term: '',
      orderBy: 'default',
      sort: placeSort,
    };
  }, [
    customDataLoader,
    topicId,
    stableUserLocation.latitude,
    stableUserLocation.longitude,
    placeSort,
  ]);

  // Data fetching - either custom or topic-based
  const [customEvents, setCustomEvents] = useState<any[]>([]);
  const [customMeetups, setCustomMeetups] = useState<any[]>([]);
  const [customPlaces, setCustomPlaces] = useState<any[]>([]);
  const [customPartners, setCustomPartners] = useState<any[]>([]);
  const [isCustomDataLoading, setIsCustomDataLoading] = useState(false);
  // For accumulating offers (pagination)
  const [accumulatedOffers, setAccumulatedOffers] = useState<any[]>([]);
  const [offersPage, setOffersPage] = useState(1); // Start at page 1
  const [hasMoreOffers, setHasMoreOffers] = useState(true);
  const hasEverLoadedCustomData = useRef(false);

  // Load custom data if customDataLoader is provided
  useEffect(() => {
    if (!customDataLoader) return;

    const loadNonOfferData = async () => {
      setIsCustomDataLoading(true);
      try {
        const [events, meetups, places, partners] = await Promise.all([
          customDataLoader.loadEvents?.() || [],
          customDataLoader.loadMeetups?.() || [],
          customDataLoader.loadPlaces?.() || [],
          customDataLoader.loadPartners?.() || [],
        ]);
        setCustomEvents(Array.isArray(events) ? events : []);
        setCustomMeetups(Array.isArray(meetups) ? meetups : []);
        setCustomPlaces(Array.isArray(places) ? places : []);
        setCustomPartners(Array.isArray(partners) ? partners : []);
        hasEverLoadedCustomData.current = true;
      } catch (error) {
        console.error('Error loading custom data:', error);
      } finally {
        setIsCustomDataLoading(false);
      }
    };
    loadNonOfferData();
  }, [customDataLoader]); // Remove the hasEverLoadedCustomData.current check - reload when customDataLoader changes

  // Effect for loading offers (accumulating for pagination)
  useEffect(() => {
    if (!customDataLoader || !customDataLoader.loadOffers) return;
    let cancelled = false;
    const loadOffers = async () => {
      setIsCustomDataLoading(true);
      try {
        // Check if loadOffers expects a page argument
        let result;
        if (customDataLoader.loadOffers?.length && customDataLoader.loadOffers.length > 0) {
          // Function expects arguments (pagination supported)
          result = await customDataLoader.loadOffers?.(offersPage);
        } else {
          // No arguments supported
          result = await customDataLoader.loadOffers?.();
        }
        const newOffers = Array.isArray(result) ? result : [];
        setAccumulatedOffers((prev) => {
          // Avoid duplicates by id
          const ids = new Set(prev.map((o) => o.id));
          return [...prev, ...newOffers.filter((o) => !ids.has(o.id))];
        });
        // If fewer than expected, assume no more offers
        if (newOffers.length === 0) setHasMoreOffers(false);
      } catch (error) {
        console.error('Error loading offers:', error);
        setHasMoreOffers(false);
      } finally {
        if (!cancelled) setIsCustomDataLoading(false);
      }
    };
    loadOffers();
    return () => {
      cancelled = true;
    };
  }, [customDataLoader, offersPage]);

  // Topic-based data fetching (always call hooks, but disable with null params when using custom loader)
  const fetchOffers = useInfiniteGlobalSearchWithParamsAndLocation(offersSearchParams);
  const fetchEvents = useInfiniteGlobalSearchWithParamsAndLocation(eventsSearchParams);
  const fetchPlaces = useInfiniteGlobalSearchWithParamsAndLocation(placesSearchParams);

  // Extract partners from event data
  const eventPartners = useMemo(() => {
    if (!event) return [];

    const partners: any[] = [];

    // Add partners from event.partners array
    if (event.partners && Array.isArray(event.partners)) {
      partners.push(...event.partners);
    }

    // Add partner from event.place.partner if it exists
    if (event.place?.partner) {
      partners.push(event.place.partner);
    }

    return partners;
  }, [event]);
  const fetchMeetups = useInfiniteMeetups();

  // Use custom data if available, otherwise use topic-based data
  const offers = useMemo(() => {
    if (customDataLoader) return accumulatedOffers;
    return fetchOffers.data?.pages.flatMap((page) => page.data.data) || [];
  }, [customDataLoader, accumulatedOffers, fetchOffers.data]);

  const events = useMemo(() => {
    if (customDataLoader) return customEvents;
    return fetchEvents.data?.pages.flatMap((page) => page.data.data) || [];
  }, [customDataLoader, customEvents, fetchEvents.data]);

  const meetups = useMemo(() => {
    // Use custom meetups if available, otherwise fall back to regular fetchMeetups endpoint
    if (customDataLoader) {
      return customMeetups;
    }

    const regularMeetups = fetchMeetups.data?.pages.flatMap((page) => page.data.data) || [];
    return regularMeetups;
  }, [customDataLoader, customMeetups, fetchMeetups.data]);

  const places = useMemo(() => {
    if (customDataLoader) return customPlaces;
    return fetchPlaces.data?.pages.flatMap((page) => page.data.data) || [];
  }, [customDataLoader, customPlaces, fetchPlaces.data]);

  // For partners - use custom data if available, otherwise use event data or filter places
  const partners = useMemo(() => {
    if (customDataLoader) return customPartners;

    // If we have event data, extract partners from it
    if (event && eventPartners.length > 0) {
      return eventPartners;
    }

    // Fallback: filter places for partners (for topic-based pages like Pride)
    return places.filter((place) => place.is_partner || place.place_type === 'partner');
  }, [customDataLoader, customPartners, event, eventPartners, places]);

  // Create numbered objects for list display and map
  const numberedObjects: NumberedListObject[] = useMemo(() => {
    const objects: NumberedListObject[] = [];

    // Add offers (will be numbered per section later)
    const processedOffers = offers.map((offer) => {
      // Use customCategory if available (for custom categorization like special offers vs insider experiences)
      const offerType = offer.customCategory || 'offer';

      // Get coordinates from place, event, or offer itself (in that order of preference)
      let latitude, longitude;
      if (offer.place?.latitude && offer.place?.longitude) {
        latitude = parseFloat(String(offer.place.latitude));
        longitude = parseFloat(String(offer.place.longitude));
      } else if (offer.event?.latitude && offer.event?.longitude) {
        latitude = parseFloat(String(offer.event.latitude));
        longitude = parseFloat(String(offer.event.longitude));
      } else if (offer.latitude && offer.longitude) {
        latitude = parseFloat(String(offer.latitude));
        longitude = parseFloat(String(offer.longitude));
      }

      return {
        ...offer,
        type: offerType,
        number: -1, // Will be set per section
        latitude,
        longitude,
        name: offer.title || 'Untitled Offer',
        data: offer,
      };
    });

    // Add events (regular events only, not meetups)
    const processedEvents = events.map((event) => {
      // Use customCategory if available (for custom categorization like comic-con vs no-badge-activations)
      const eventType = event.customCategory || 'event';

      return {
        ...event,
        type: eventType,
        originalType: event.originalType || 'event', // Preserve original type for card rendering
        number: -1, // Will be set per section
        latitude: event.latitude ? parseFloat(String(event.latitude)) : undefined,
        longitude: event.longitude ? parseFloat(String(event.longitude)) : undefined,
        name: event.title || 'Untitled Event',
        data: event,
      };
    });

    // Add meetups (using dedicated fetchMeetups endpoint)
    const processedMeetups = meetups.map((meetup) => ({
      ...meetup,
      type: 'meetup' as const,
      number: -1, // Will be set per section
      latitude: meetup.latitude ? parseFloat(String(meetup.latitude)) : undefined,
      longitude: meetup.longitude ? parseFloat(String(meetup.longitude)) : undefined,
      name: meetup.title || 'Untitled Meetup',
      data: meetup,
    }));

    // Add partners
    const processedPartners = partners.map((partner) => ({
      ...partner,
      type: 'partner' as const,
      number: -1, // Will be set per section
      latitude: partner.latitude ? parseFloat(String(partner.latitude)) : undefined,
      longitude: partner.longitude ? parseFloat(String(partner.longitude)) : undefined,
      name: partner.name || 'Untitled Partner',
      data: partner,
    }));

    // Add places with custom categorization support
    const processedPlaces = places
      .filter((place) => !place.is_partner && place.place_type !== 'partner')
      .map((place) => {
        let category = 'place';
        // Preserve any existing custom icon or set it based on category
        let customIcon: string | undefined = place.customIcon;

        // Check if place already has a category (from custom data loader)
        if (place.category) {
          category = place.category;
        } else if (
          config.customCategorization?.enabled &&
          config.customCategorization?.categoryFunction
        ) {
          // Apply custom categorization if enabled
          category = config.customCategorization.categoryFunction(place);
        }

        // Special handling for parking (uses "P" icon instead of number)
        if (category === 'parking' && !customIcon) {
          customIcon = 'P';
        }

        //console.log('Processing place with customIcon:', place.name, customIcon);

        const processedPlace = {
          ...place,
          type: category,
          category,
          customIcon,
          originalType: place.originalType || 'place', // Preserve original type for card rendering
          number: category === 'parking' ? -1 : -1, // Will be set per section (parking always uses "P")
          latitude: place.latitude ? parseFloat(String(place.latitude)) : undefined,
          longitude: place.longitude ? parseFloat(String(place.longitude)) : undefined,
          name: place.name || 'Untitled Place',
          data: place,
        };

        return processedPlace;
      });

    objects.push(
      ...processedOffers,
      ...processedEvents,
      ...processedMeetups,
      ...processedPartners,
      ...processedPlaces
    );

    // Sort by distance if user location is available and sorting is enabled
    return config.sections.offers?.sortBy === 'distance'
      ? sortByDistanceFromUser(objects)
      : objects;
  }, [
    offers,
    events,
    meetups,
    partners,
    places,
    sortByDistanceFromUser,
    config.sections.offers?.sortBy,
    config.customCategorization,
  ]);

  // Handle scrolling to anchor links after content loads
  useEffect(() => {
    const scrollToAnchor = () => {
      const hash = window.location.hash;
      if (hash) {
        const targetId = hash.substring(1).toLowerCase(); // Remove the # symbol and convert to lowercase
        const element = document.getElementById(targetId);
        if (element) {
          // Find the section key that corresponds to this anchor
          const matchingSection = Object.entries(config.sections).find(([sectionKey, section]) => {
            const sectionAnchorId = section.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '');
            return sectionAnchorId === targetId;
          });

          // Expand the section if found
          if (matchingSection) {
            const [sectionKey] = matchingSection;
            setExpandedSections((prev) => ({
              ...prev,
              [sectionKey]: true,
            }));

            // Wait a bit for the section to expand before scrolling
            setTimeout(() => {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }, 100);
          } else {
            // If no matching section found, scroll immediately
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }
    };

    const handleHashChange = () => {
      setTimeout(scrollToAnchor, 100);
    };

    // Scroll to anchor on initial load or when content changes
    const timeoutId = setTimeout(scrollToAnchor, 100);

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [numberedObjects]); // Re-run when content changes

  // Separate objects by type for display - supports dynamic section types
  const objectsByType = useMemo(() => {
    const result: Record<string, NumberedListObject[]> = {};

    // Initialize with all enabled sections
    Object.keys(config.sections).forEach((sectionKey) => {
      if (config.sections[sectionKey].enabled) {
        result[sectionKey] = [];
      }
    });

    // Categorize objects
    numberedObjects.forEach((obj) => {
      if (obj.type === 'event' && result.events) {
        result.events.push(obj);
      } else if (obj.type === 'meetup' && result.meetups) {
        result.meetups.push(obj);
      } else if (obj.type === 'offer' && result.offers) {
        // Handle singular 'offer' type -> plural 'offers' section
        result.offers.push(obj);
      } else if (obj.type === 'partner' && result.partners) {
        // Handle singular 'partner' type -> plural 'partners' section
        result.partners.push(obj);
      } else if (obj.type === 'place' && result.places) {
        // Handle singular 'place' type -> plural 'places' section
        result.places.push(obj);
      } else if (result[obj.type]) {
        // Handle exact matches (like custom categories: parking, murals, etc.)
        result[obj.type].push(obj);
      }
    });

    // Assign sequential numbers across all sections in a defined order
    let globalCounter = 0;
    // Process sections in a consistent order to ensure sequential numbering
    // Use the order from config.sections instead of a hardcoded order
    const sectionOrder = Object.keys(config.sections).filter((key) => config.sections[key].enabled);

    // First process the standard sections in order
    sectionOrder.forEach((sectionKey) => {
      if (result[sectionKey]) {
        result[sectionKey].forEach((obj) => {
          // Don't number parking items (they use "P" icon)
          if (obj.customIcon !== 'P') {
            obj.number = globalCounter++;
          }
        });
      }
    });

    // Then process any custom sections not in the main config
    Object.keys(result).forEach((sectionKey) => {
      if (!sectionOrder.includes(sectionKey)) {
        result[sectionKey].forEach((obj) => {
          // Don't number parking items (they use "P" icon)
          if (obj.customIcon !== 'P') {
            obj.number = globalCounter++;
          }
        });
      }
    });

    return result;
  }, [numberedObjects, config.sections]);

  // Create map data for MapView component (if map is enabled)
  const mapArticles = useMemo(() => {
    if (config.layout !== 'map-and-list' || !config.mapConfig?.enabled) {
      return [];
    }

    return numberedObjects
      .filter((obj) => obj.latitude && obj.longitude)
      .map((obj) => {
        // Map object type to section key (handle singular/plural mismatches)
        let sectionKey = obj.type;
        if (obj.type === 'offer') sectionKey = 'offers';
        else if (obj.type === 'partner') sectionKey = 'partners';
        else if (obj.type === 'place') sectionKey = 'places';
        else if (obj.type === 'meetup') sectionKey = 'meetups';
        else if (obj.type === 'event') {
          sectionKey = obj.is_meetup ? 'meetups' : 'events';
        }

        // Get the section configuration for color
        const section = config.sections[sectionKey] || config.sections.places;
        // Use gradient middle color if circleBackgroundImage is set, otherwise use circleColor
        const pinColor = section?.circleBackgroundImage
          ? '#c471f5'
          : section?.circleColor || '#3182ce';

        return {
          ...obj,
          pinColor,
          // Use custom icon (like "P") or number
          customIcon: obj.customIcon,
          number: obj.customIcon ? undefined : obj.number >= 0 ? obj.number + 1 : undefined,
        };
      });
  }, [numberedObjects, config.layout, config.mapConfig, config.sections]);

  // User location for map
  const memoizedUserLocation = useMemo(() => {
    if (!stableUserLocation.latitude || !stableUserLocation.longitude) {
      return undefined;
    }
    return {
      latitude: stableUserLocation.latitude,
      longitude: stableUserLocation.longitude,
    };
  }, [stableUserLocation.latitude, stableUserLocation.longitude]);

  // Helper function to sort items within a section
  const sortSectionItems = useCallback(
    (items: NumberedListObject[], sortBy?: 'distance' | 'date' | 'alphabetical') => {
      if (!sortBy) return items;

      switch (sortBy) {
        case 'distance':
          return sortByDistanceFromUser(items);
        case 'date':
          return [...items].sort((a, b) => {
            const dateA = a.data.start_date_time || a.data.starts_at || a.data.created_at;
            const dateB = b.data.start_date_time || b.data.starts_at || b.data.created_at;
            if (!dateA || !dateB) return 0;
            return new Date(dateA).getTime() - new Date(dateB).getTime();
          });
        case 'alphabetical':
          return [...items].sort((a, b) => a.name.localeCompare(b.name));
        default:
          return items;
      }
    },
    [sortByDistanceFromUser]
  );

  // Automatic pagination for meetups is no longer needed since meetups are fetched separately
  // using the dedicated fetchMeetups endpoint rather than being filtered from events

  // Check if any data is still loading
  const isAnyDataLoading = useMemo(() => {
    // Always check user location loading
    //if (userLocation.isLoading) return true;

    if (customDataLoader) {
      // For custom data loader, we need to be more nuanced
      // If we're in the middle of custom loading, show spinner
      if (isCustomDataLoading) return true;

      // If we haven't ever loaded custom data yet, show spinner
      // This prevents showing "no content" before the first load attempt
      if (!hasEverLoadedCustomData.current) return true;

      // If we've loaded data (even if empty), don't show loading spinner
      return false;
    }

    // For topic-based loading, use standard loading checks
    return (
      fetchOffers.isLoading ||
      fetchEvents.isLoading ||
      fetchMeetups.isLoading ||
      fetchPlaces.isLoading
    );
  }, [
    userLocation.isLoading,
    customDataLoader,
    isCustomDataLoading,
    accumulatedOffers.length,
    customEvents.length,
    customPlaces.length,
    customPartners.length,
    fetchOffers.isLoading,
    fetchEvents.isLoading,
    fetchMeetups.isLoading,
    fetchPlaces.isLoading,
  ]);

  // Automatic pagination for meetups - load additional pages if no meetups found
  // Show unified loading spinner if any data is still loading (after all hooks are called)
  if (isAnyDataLoading) {
    return (
      <Center py={8}>
        <SpinnerLoader />
      </Center>
    );
  }

  // Helper function to render a section
  const renderSection = (
    sectionKey: keyof CuratedEventConfig['sections'],
    items: NumberedListObject[]
  ) => {
    const section = config.sections[sectionKey];

    // Don't render if section is disabled
    if (!section.enabled) return null;

    // For sections with custom header content, always render even if no items
    // For regular sections, only render if there are items
    if (items.length === 0 && !section.customHeaderContent?.showInsiderClubPromo) return null;

    // Create anchor ID from section title (lowercase, replace spaces with hyphens)
    const anchorId = section.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const sectionExpanded = expandedSections[sectionKey] || false;

    return (
      <Box key={sectionKey} id={anchorId} mt={'0.5rem !important'}>
        {/* Expandable Section Header */}
        <Box
          as={section.customHeaderContent?.showInsiderClubPromo ? 'div' : 'button'}
          onClick={
            section.customHeaderContent?.showInsiderClubPromo
              ? undefined
              : () => toggleSection(String(sectionKey))
          }
          width="100%"
          padding="15px"
          backgroundImage="linear-gradient(to bottom, #f8fbff, #e9f3ff)"
          borderRadius="8px"
          fontWeight="bold"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom="8px"
          boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
          border="1px solid #cce0ff"
          textAlign="left"
          transition="all 0.2s"
          _hover={
            section.customHeaderContent?.showInsiderClubPromo
              ? {}
              : {
                  backgroundColor: '#e9f3ff',
                }
          }
          css={{
            WebkitTapHighlightColor: 'transparent',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}>
          <HStack spacing={3} align="center">
            <Text fontSize="xl" role="img" aria-label={String(sectionKey)}>
              {section.icon}
            </Text>
            <Text fontWeight="bold" fontSize="lg" color={section.circleColor}>
              {section.title}
            </Text>
          </HStack>
          <Text whiteSpace="nowrap" fontSize="lg" fontWeight="bold" color={section.circleColor}>
            {items.length}
          </Text>
        </Box>

        {/* Custom Header Content for Non-Members - always visible when enabled */}
        {section.customHeaderContent?.showInsiderClubPromo && (
          <Box
            backgroundColor="#f7fafc"
            borderRadius="8px"
            padding="16px"
            marginBottom="16px"
            border="1px solid #e2e8f0">
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.700" lineHeight="1.5">
                Join the Local Insider's Club to get hundreds of dollars of exclusive perks, VIP
                experiences, extra Walkabout points, and more.
              </Text>

              <VStack spacing={2} align="stretch">
                {section.customHeaderContent.membershipUrl && (
                  <BlueButton
                    href={section.customHeaderContent.membershipUrl}
                    onClick={(e) => {
                      section?.customHeaderContent?.membershipUrl
                        ? navigate(section.customHeaderContent.membershipUrl)
                        : null;
                    }}>
                    Become an Insider
                  </BlueButton>
                )}

                {section.customHeaderContent.showPreviewButton && (
                  <WhiteButton
                    onClick={() => {
                      // Toggle section expansion when preview button is clicked
                      toggleSection(String(sectionKey));
                      section.customHeaderContent?.onPreviewToggle?.();
                    }}
                    size="sm"
                    variant="outline"
                    borderColor="#4299e1"
                    color="#4299e1"
                    _hover={{backgroundColor: '#ebf8ff'}}
                    fontSize="sm"
                    fontWeight="600">
                    {sectionExpanded
                      ? 'Hide Preview'
                      : section.customHeaderContent.previewButtonText ||
                        'Preview Insider Perks Nearby'}
                  </WhiteButton>
                )}
              </VStack>
            </VStack>
          </Box>
        )}

        {/* Section Content - Collapsible */}
        <Collapse in={sectionExpanded}>
          {/* Section Items */}
          <VStack spacing={2} align="stretch">
            {/* Show preview message for non-members when preview is expanded */}
            {section.customHeaderContent?.showInsiderClubPromo && sectionExpanded && (
              <Box
                padding="12px"
                textAlign="center"
                backgroundColor="#fff3cd"
                borderRadius="8px"
                border="1px solid #ffeaa7"
                marginBottom="8px">
                <Text color="#856404" fontSize="sm" fontWeight="500">
                  🔒 Preview Mode - Join the Local Insider's Club to unlock these exclusive perks
                </Text>
              </Box>
            )}

            {/* Show actual items when preview is expanded or user is a member */}
            {(section.customHeaderContent?.showInsiderClubPromo && sectionExpanded) ||
            !section.customHeaderContent?.showInsiderClubPromo
              ? items.map((item, index) => {
                  return (
                    <Box key={item.id} mt="0 !important">
                      <Box
                        padding="15px"
                        backgroundColor="white"
                        borderRadius="8px"
                        border="1px solid #e2e8f0"
                        boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)">
                        <HStack
                          spacing={3}
                          align={
                            getListItemStyle() === listItemStyles.CARD ? 'center' : 'baseline'
                          }>
                          {/* Number badge and distance - left side */}
                          <VStack spacing={1} align="center" flexShrink={0}>
                            {item.customIcon === 'FARMERS_MARKET_CENTER' ? (
                              // Special farmers market icon in circle
                              <Box
                                w="32px"
                                h="32px"
                                borderRadius="full"
                                overflow="hidden"
                                position="relative">
                                <svg
                                  width="32"
                                  height="32"
                                  viewBox="0 0 32 32"
                                  xmlns="http://www.w3.org/2000/svg">
                                  {/* Green background */}
                                  <circle cx="16" cy="16" r="16" fill="#2E8B57" />

                                  {/* White circle inside */}
                                  <circle cx="16" cy="16" r="12" fill="#FFFFFF" />

                                  {/* Market basket symbol - simplified */}
                                  <path
                                    d="M8 19L24 19L21.5 11L10.5 11Z"
                                    fill="#2E8B57"
                                    stroke="#333333"
                                    stroke-width="0.5"
                                  />
                                  <path
                                    d="M10.5 11C10.5 11 13 7 21.5 11"
                                    fill="none"
                                    stroke="#333333"
                                    stroke-width="1.2"
                                    stroke-linecap="round"
                                  />

                                  {/* Simple produce */}
                                  <circle cx="13" cy="15.5" r="2" fill="#8BC34A" />
                                  <circle cx="16" cy="15" r="2" fill="#FF5722" />
                                  <circle cx="19" cy="15.5" r="2" fill="#FFEB3B" />
                                </svg>
                              </Box>
                            ) : (
                              // Regular number or "P" icon
                              <Flex
                                bg={section.circleBackgroundImage ? undefined : section.circleColor}
                                backgroundImage={section.circleBackgroundImage}
                                color="white"
                                borderRadius="full"
                                w="32px"
                                h="32px"
                                align="center"
                                justify="center"
                                fontSize="sm"
                                fontWeight="bold">
                                {item.customIcon || (item.number >= 0 ? item.number + 1 : '')}
                              </Flex>
                            )}

                            {/* Distance below badge */}
                            {item.latitude &&
                              item.longitude &&
                              stableUserLocation.latitude &&
                              stableUserLocation.longitude && (
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  textAlign="center"
                                  lineHeight="1.2">
                                  {getDistanceString(
                                    calculateDistance(
                                      {
                                        latitude: stableUserLocation.latitude,
                                        longitude: stableUserLocation.longitude,
                                      },
                                      {
                                        latitude: item.latitude,
                                        longitude: item.longitude,
                                      }
                                    )
                                  )}
                                </Text>
                              )}
                          </VStack>

                          {getListItemStyle() === listItemStyles.CARD ? (
                            <Box flex={1} minW={0}>
                              {item.type === 'offer' && <OfferCard offer={item.data} />}
                              {item.type === 'offers' && <OfferCard offer={item.data} />}
                              {item.type === 'special-offers' && <OfferCard offer={item.data} />}
                              {item.type === 'meetup' && <MeetUpCard meet-up={item.data} />}
                              {(item.type === 'event' || item.originalType === 'event') && (
                                <EventCard event={item.data} />
                              )}
                              {item.type === 'partner' && <PartnerCard partner={item.data} />}
                              {(item.type === 'place' ||
                                item.originalType === 'place' ||
                                item.type === 'places' ||
                                item.type === 'parking' ||
                                item.type === 'murals' ||
                                item.type === 'food-beverage' ||
                                item.type === 'shopping' ||
                                item.type === 'services' ||
                                item.category) && <PlaceCard place={item.data} />}
                            </Box>
                          ) : (
                            <HStack flex={1} minW={0} spacing={3} align="center">
                              <Box flex={1} minW={0}>
                                {item.type === 'offer' || item.type === 'offers' ? (
                                  <OfferCard offer={item.data} />
                                ) : (
                                  <TextG16
                                    fontWeight="500"
                                    textAlign={'center'}
                                    textDecoration={
                                      checkedItems[item.id] && section.checkboxEnabled
                                        ? 'line-through'
                                        : 'none'
                                    }
                                    color={
                                      checkedItems[item.id] && section.checkboxEnabled
                                        ? 'gray.500'
                                        : 'inherit'
                                    }
                                    transition="all 0.2s ease">
                                    {item.data.name || item.data.description}
                                  </TextG16>
                                )}
                              </Box>

                              {/* Checkbox for text-style items */}
                              {section.checkboxEnabled && (
                                <HStack spacing={2} align="center" flexShrink={0}>
                                  <Text fontSize="sm" color="gray.600" fontWeight="500">
                                    Tried It?
                                  </Text>
                                  <Checkbox
                                    isChecked={checkedItems[item.id] || false}
                                    onChange={() => toggleItemChecked(item.id)}
                                    size="lg"
                                    colorScheme="green"
                                  />
                                </HStack>
                              )}
                            </HStack>
                          )}
                        </HStack>

                        {/* Get Directions Button */}
                        {(() => {
                          const directionsLink = getDirectionsLinkForItem(item);
                          if (!directionsLink) return null;

                          if (getDirectionsLinkStyle() === directionsLinkStyles.TEXT) {
                            return (
                              <Box mt={3} pt={3} borderTop="1px solid #e2e8f0" textAlign="center">
                                <TextG16
                                  color={theme.colors.blue}
                                  cursor="pointer"
                                  textDecoration="underline"
                                  onClick={() =>
                                    window.open(directionsLink, '_blank', 'noopener,noreferrer')
                                  }>
                                  Get Directions
                                </TextG16>
                              </Box>
                            );
                          }
                          return (
                            <Box mt={3} pt={3} borderTop="1px solid #e2e8f0">
                              <BlueButton
                                onClick={() =>
                                  window.open(directionsLink, '_blank', 'noopener,noreferrer')
                                }
                                width="100%">
                                <TextG16 color="white !important">Get Directions</TextG16>
                              </BlueButton>
                            </Box>
                          );
                        })()}
                      </Box>

                      {/* Divider between items (except last item) */}
                      {index < items.length - 1 && (
                        <Box height="1px" backgroundColor="gray.200" marginY="16px" width="100%" />
                      )}
                    </Box>
                  );
                })
              : null}
          </VStack>
        </Collapse>
      </Box>
    );
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Banner Section */}
      {config.title && (config.bannerImage || config.bannerBackgroundColor) && (
        <Box
          position="relative"
          borderRadius="md"
          overflow="hidden"
          boxShadow="md"
          background={config.bannerBackgroundColor || 'gray.100'}
          minH="120px">
          {config.bannerImage && (
            <Image
              src={config.bannerImage}
              alt={`${config.title} Banner`}
              width="100%"
              height="120px"
              objectFit="cover"
            />
          )}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0,0,0,0.3)"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <Text
              whiteSpace="normal !important"
              textAlign="left"
              fontWeight="bold"
              fontSize="2xl"
              color={config.bannerTextColor || 'white'}
              textShadow="2px 2px 4px rgba(0,0,0,0.5)">
              {config.title}
            </Text>
          </Box>
        </Box>
      )}

      {/* Map Section */}
      {config.layout === 'map-and-list' && config.mapConfig?.enabled && mapArticles.length > 0 && (
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={1}>
            {config.mapConfig.title || 'Map'}
          </Text>
          <TextG16 color={theme.colors.grey} mb={3}>
            Tap the pins or scroll below the map for details.
          </TextG16>
          <Box borderRadius="md" overflow="hidden" boxShadow="md">
            <MapView
              articles={mapArticles}
              userLocation={memoizedUserLocation}
              center={config.mapConfig.center}
              initialZoom={config.mapConfig.initialZoom}
              height={config.mapConfig.height || '400px'}
            />
          </Box>
        </Box>
      )}

      {/* Show loading spinner if data is still loading */}
      {isAnyDataLoading ? (
        <Center py={8}>
          <SpinnerLoader />
        </Center>
      ) : (
        <>
          {/* Content Sections - Dynamic rendering */}
          <VStack spacing={4} align="stretch">
            {Object.entries(config.sections).map(([sectionKey, sectionConfig]) => {
              if (!sectionConfig.enabled) return null;

              const sectionItems = objectsByType[sectionKey] || [];
              const sortedItems = sortSectionItems(sectionItems, sectionConfig.sortBy);

              if (sortedItems.length === 0) return null;

              return renderSection(sectionKey as keyof CuratedEventConfig['sections'], sortedItems);
            })}

            {/* No content message - only show when not loading and no content */}
            {numberedObjects.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray">No content found for {config.title}.</Text>
              </Box>
            )}
          </VStack>
        </>
      )}
    </VStack>
  );
};

export default React.memo(CuratedEventContent);
